import { useState, useEffect, useRef } from "react";
import { Input } from "../ui/input";
import api from "../../lib/api";
import {
  Table,
  TableRow,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
} from "../ui/table";
import { Button } from "../ui/button";
import Pagination from "../ui/pagination";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Loader } from "../layout/Loader";
import { Check, Pencil, X, UploadIcon, UploadCloud } from "lucide-react";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../lib/authContext";
import { MASTER_MEDS_COLUMNS } from "../../lib/constants";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../ui/dialog";
import { Label } from "../ui/label";
import * as XLSX from "xlsx";

export default function MasterMeds() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [masterMeds, setMasterMeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMedData, setEditMedData] = useState<any>({});
  const { toast } = useToast();
  const { excelPermissions } = useAuth();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [filePreview, setFilePreview] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newMed, setNewMed] = useState<any>({
    drugcode: "",
    drugname: "",
    drugquantity: "",
    drugmeasure: "",
    drugform: "",
    packform: "",
    mrp: "",
    brand: "",
    manufacturer: "",
    salt: "",
    fastmoving: false,
  });

  const getMasterMeds = () => {
    setLoading(true);
    api.getMasterMeds(search, page, limit).then((res: any) => {
      setMasterMeds(res.data);
      setTotal(res.totalCount);
      setLoading(false);
    });
  };

  useEffect(() => {
    getMasterMeds();
  }, [page, limit]);

  useEffect(() => {
    if (search.length > 2) {
      getMasterMeds();
    }
  }, [search]);

  const updateMed = (id: string, data: any) => {
    api.updateMasterMed({ id, ...data }).then((res: any) => {
      if (res.success) {
        toast({
          title: "Medication updated successfully",
          description: res.message,
        });
        getMasterMeds();
      } else {
        toast({
          title: "Error updating medication",
          description: res.message,
        });
      }
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (
        file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        toast({
          title: "Invalid file format",
          description: "Please upload only .xlsx files",
          variant: "destructive",
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      setFilePreview([]);
      setShowPreview(false);
    }
  };

  const generatePreview = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = e => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData: any = XLSX.utils.sheet_to_json(worksheet);

        const requiredFields = ["drugcode", "drugname", "drugquantity", "drugmeasure", "mrp"];
        const missingFields = requiredFields.filter(
          field => !jsonData[0] || (
            !(field in jsonData[0]) &&
            !(field.charAt(0).toUpperCase() + field.slice(1) in jsonData[0])
          )
        );

        if (missingFields.length > 0) {
          toast({
            title: "Missing required fields",
            description: `Please ensure the following fields are present: ${missingFields.join(
              ", ",
            )}`,
            variant: "destructive",
          });
          setIsUploading(false);
          return;
        }

        const previewData = jsonData.slice(0, 10);
        setFilePreview(previewData);
        setShowPreview(true);
        setIsUploading(false);
      };

      reader.readAsBinaryString(selectedFile);
    } catch (error) {
      toast({
        title: "Preview failed",
        description: "An error occurred while generating preview",
        variant: "destructive",
      });
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await api.uploadMasterMedsExcel(formData);

      if (response && response.success) {
        // Check if there were any errors during upload
        if (response.data?.errorCount > 0) {
          const errors = response.data.errors || [];
          const duplicateErrors = errors.filter((error: string) =>
            error.includes("already exists")
          );
          const missingFieldErrors = errors.filter((error: string) =>
            error.includes("required field")
          );
          const otherErrors = errors.filter((error: string) =>
            !error.includes("already exists") && !error.includes("required field")
          );

          let errorDescription = response.message;

          if (duplicateErrors.length > 0) {
            errorDescription += `\n\nDuplicate drug codes:`;
            duplicateErrors.slice(0, 5).forEach((error: string) => {
              const match = error.match(/Drug code '([^']+)' already exists/);
              if (match) {
                errorDescription += `\n• Drug code '${match[1]}' already exists`;
              } else {
                errorDescription += `\n• ${error}`;
              }
            });
            if (duplicateErrors.length > 5) {
              errorDescription += `\n• ... and ${duplicateErrors.length - 5} more duplicate drug code(s)`;
            }
          }

          if (missingFieldErrors.length > 0) {
            errorDescription += `\n\nValidation errors:`;
            missingFieldErrors.slice(0, 3).forEach((error: string) => {
              errorDescription += `\n• ${error}`;
            });
            if (missingFieldErrors.length > 3) {
              errorDescription += `\n• ... and ${missingFieldErrors.length - 3} more validation error(s)`;
            }
          }

          if (otherErrors.length > 0) {
            errorDescription += `\n\nOther errors:`;
            otherErrors.slice(0, 2).forEach((error: string) => {
              errorDescription += `\n• ${error}`;
            });
            if (otherErrors.length > 2) {
              errorDescription += `\n• ... and ${otherErrors.length - 2} more error(s)`;
            }
          }

          toast({
            title: response.data.successCount > 0 ? "Partial upload completed" : "Upload failed",
            description: errorDescription,
            variant: response.data.successCount > 0 ? "default" : "destructive",
          });
        } else {
          toast({
            title: "Excel file uploaded successfully",
            description: response.message || "Products uploaded to master medications",
          });
        }

        if (response.data?.successCount > 0) {
          getMasterMeds();
          setUploadModalOpen(false);
          resetUploadForm();
        }
      } else if (response && !response.success) {
        // Handle specific backend validation errors
        let errorMessage = response.error || "An error occurred while uploading the file";

        // Check for specific error types and provide user-friendly messages
        if (errorMessage.includes("Drug code already exists")) {
          errorMessage = "Drug code already exists. Please use a different drug code.";
        } else if (errorMessage.includes("required field")) {
          errorMessage = errorMessage;
        } else if (errorMessage.includes("Missing required column headers")) {
          errorMessage = errorMessage;
        }

        toast({
          title: "Upload failed",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Upload failed",
          description: "No response received from server",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error?.message || "An error occurred while uploading the file",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setShowPreview(false);
    setFilePreview([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddMed = async () => {
    const requiredFields: { [key: string]: string } = {
      drugcode: "Drug Code",
      drugname: "Drug Name",
      drugquantity: "Drug Quantity",
      drugmeasure: "Measure",
      mrp: "MRP",
    };

    for (const field of Object.keys(requiredFields)) {
      if (!newMed[field]) {
        toast({
          title: "Validation Error",
          description: `${requiredFields[field]} is a required field.`,
          variant: "destructive",
        });
        return;
      }
    }
    // Sanitize payload: convert empty strings to null, ensure numbers are numbers or null
    const sanitizedMed = {
      ...newMed,
      drugquantity: newMed.drugquantity === "" ? null : Number(newMed.drugquantity),
      mrp: newMed.mrp === "" ? null : Number(newMed.mrp),
      drugmeasure: newMed.drugmeasure === "" ? null : newMed.drugmeasure,
      drugform: newMed.drugform === "" ? null : newMed.drugform,
      packform: newMed.packform === "" ? null : newMed.packform,
      brand: newMed.brand === "" ? null : newMed.brand,
      manufacturer: newMed.manufacturer === "" ? null : newMed.manufacturer,
      salt: newMed.salt === "" ? null : newMed.salt,
      drugcode: newMed.drugcode === "" ? null : newMed.drugcode,
      drugname: newMed.drugname === "" ? null : newMed.drugname,
      fastmoving: !!newMed.fastmoving,
    };
    const res = await api.createMasterMed(sanitizedMed);
    if (res && res.success) {
      toast({
        title: "Medication added successfully",
        description: res.message,
      });
      setAddModalOpen(false);
      setNewMed({
        drugcode: "",
        drugname: "",
        drugquantity: "",
        drugmeasure: "",
        drugform: "",
        packform: "",
        mrp: "",
        brand: "",
        manufacturer: "",
        salt: "",
        fastmoving: false,
      });
      getMasterMeds();
    } else {
      let errorMsg = res?.message || res?.error || "Unknown error";
      if (errorMsg === "Drug code already exists") {
        toast({
          title: "Drug code already exists",
          description: "A product with this drug code already exists. Please use a different drug code.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error adding medication",
          description: errorMsg,
        });
      }
    }
  };

  return (
    <div className="container mx-auto p-4 bg-white rounded-md shadow-md dark:bg-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-black dark:text-white">
          Master Medications
        </h1>
        <div className="flex gap-2">
          <Dialog
            open={uploadModalOpen}
            onOpenChange={open => {
              setUploadModalOpen(open);
              if (!open) resetUploadForm();
            }}
          >
            {excelPermissions.canUploadExcel && (
              <DialogTrigger asChild>
                <Button variant="outline" onClick={() => setUploadModalOpen(true)}>
                  <UploadIcon className="mr-2 h-4 w-4" /> Upload Excel
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className={`${showPreview ? 'max-w-4xl w-full h-[80vh]' : 'max-w-lg'} flex flex-col`}>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Upload Master Medications Excel</DialogTitle>
            </DialogHeader>

            {/* Upload Section */}
            <div className={`${showPreview ? 'flex-1 min-h-0' : ''} flex flex-col gap-4 py-4`}>
              <div
                className={`flex-shrink-0 flex flex-col items-center justify-center border-2 border-dashed border-gray-500 rounded-md ${showPreview ? 'h-32' : 'h-40'} cursor-pointer hover:bg-gray-900 transition`}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadCloud className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-xs text-gray-300">
                  <span className="text-blue-400 underline">Browse</span> or drag a file here
                </p>

                <Input
                  id="file"
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <p className="text-xs text-gray-500 mt-2 text-center max-w-xs px-2">
                  Upload Excel file (.xlsx) with master medication data<br />
                  Required fields: <strong>drugcode, drugname, drugquantity, drugmeasure, mrp</strong>
                </p>
              </div>

              {/* Preview Table */}
              {showPreview && filePreview.length > 0 && (
                <div className="flex-1 flex flex-col min-h-0">
                  <h3 className="text-lg font-medium mb-2 flex-shrink-0">File Preview</h3>
                  <div className="flex-1 border rounded-md overflow-hidden min-h-0">
                    <div className="h-full overflow-auto">
                      <Table className="w-full">
                        <TableHeader className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10">
                          <TableRow>
                            {Object.keys(filePreview[0]).map((header) => (
                              <TableHead
                                key={header}
                                className="whitespace-nowrap px-3 py-2 text-sm font-medium min-w-[100px]"
                                title={header}
                              >
                                {header}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filePreview.map((row: any, index: number) => (
                            <TableRow key={index}>
                              {Object.values(row).map((cell: any, cellIndex: number) => (
                                <TableCell
                                  key={cellIndex}
                                  className="whitespace-nowrap px-3 py-2 text-sm"
                                  title={String(cell || '')}
                                >
                                  {cell || ''}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2 flex-shrink-0">
                    Showing {filePreview.length} rows from the file. Scroll to view all content.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter className="flex-shrink-0">
              {!showPreview ? (
                <Button onClick={generatePreview} disabled={!selectedFile || isUploading}>
                  {isUploading ? "Processing..." : "Preview Data"}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPreview(false);
                      setFilePreview([]);
                    }}
                  >
                    Back
                  </Button>
                  <Button onClick={handleUpload} disabled={isUploading}>
                    {isUploading ? "Uploading..." : "Confirm Upload"}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>

          </Dialog>
          <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setAddModalOpen(true)}>
                Add Products
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Master Medication</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="drugcode">
                  Drug Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="drugcode"
                  placeholder="Drug Code"
                  value={newMed.drugcode}
                  onChange={(e) =>
                    setNewMed({ ...newMed, drugcode: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="drugname">
                  Drug Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="drugname"
                  placeholder="Drug Name"
                  value={newMed.drugname}
                  onChange={(e) =>
                    setNewMed({ ...newMed, drugname: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="drugquantity">
                  Drug Quantity <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="drugquantity"
                  placeholder="Drug Quantity"
                  type="number"
                  value={newMed.drugquantity}
                  onChange={(e) =>
                    setNewMed({ ...newMed, drugquantity: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="drugmeasure">
                  Measure <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="drugmeasure"
                  placeholder="Measure"
                  value={newMed.drugmeasure}
                  onChange={(e) =>
                    setNewMed({ ...newMed, drugmeasure: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="drugform">Form</Label>
                <Input
                  id="drugform"
                  placeholder="Form"
                  value={newMed.drugform}
                  onChange={(e) =>
                    setNewMed({ ...newMed, drugform: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="packform">Pack Form</Label>
                <Input
                  id="packform"
                  placeholder="Pack Form"
                  value={newMed.packform}
                  onChange={(e) =>
                    setNewMed({ ...newMed, packform: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="mrp">
                  MRP <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="mrp"
                  placeholder="MRP"
                  type="number"
                  value={newMed.mrp}
                  onChange={(e) => setNewMed({ ...newMed, mrp: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="brand">Brand</Label>
                <Input
                  id="brand"
                  placeholder="Brand"
                  value={newMed.brand}
                  onChange={(e) =>
                    setNewMed({ ...newMed, brand: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  placeholder="Manufacturer"
                  value={newMed.manufacturer}
                  onChange={(e) =>
                    setNewMed({ ...newMed, manufacturer: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="salt">Salt</Label>
                <Input
                  id="salt"
                  placeholder="Salt"
                  value={newMed.salt}
                  onChange={(e) =>
                    setNewMed({ ...newMed, salt: e.target.value })
                  }
                />
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Switch
                  checked={newMed.fastmoving}
                  onCheckedChange={(checked) =>
                    setNewMed({ ...newMed, fastmoving: checked })
                  }
                />
                <span>Is Fast Moving?</span>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleAddMed}>Submit</Button>
              <DialogClose asChild>
                <Button variant="outline" onClick={() => setAddModalOpen(false)}>Cancel</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="w-1/4 my-4 relative">
          <Input
            placeholder="Search for a product"
            id="search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Button
            className={`absolute right-0 top-0 bg-none ${
              search.length > 0 ? "cursor-pointer" : "cursor-not-allowed"
            }`}
            onClick={() => {
              setSearch("");
              getMasterMeds();
            }}
            disabled={search.length === 0}
          >
            <X size={20} />
          </Button>
        </div>
      </div>
      <div className="w-full bg-white rounded-md shadow-md dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              {MASTER_MEDS_COLUMNS.map((column: string) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
              <TableHead>Is Fast Moving?</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : (
              masterMeds.map((med: any) => (
                <TableRow key={med.id}>
                  {editMedData.id === med.id
                    ? MASTER_MEDS_COLUMNS.map((column: string) => (
                        <TableCell key={column}>
                          <Input
                            value={editMedData[column]}
                            onChange={e =>
                              setEditMedData({
                                ...editMedData,
                                [column]: e.target.value,
                              })
                            }
                          />
                        </TableCell>
                      ))
                    : MASTER_MEDS_COLUMNS.map((column: string) => (
                        <TableCell key={column}>{med[column]}</TableCell>
                      ))}
                  <TableCell>
                    <Switch
                      checked={med.fastmoving}
                      onCheckedChange={() => {
                        updateMed(med.id, { fastmoving: !med.fastmoving });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {editMedData.id === med.id ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            updateMed(med.id, editMedData);
                            setEditMedData({});
                          }}
                          className="bg-green-500 text-white"
                        >
                          <Check size={20} />
                        </Button>
                        <Button
                          onClick={() => {
                            setEditMedData({});
                          }}
                          className="bg-red-500 text-white"
                        >
                          <X size={20} />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          setEditMedData(med);
                        }}
                        className="text-white dark:bg-gray-700"
                      >
                        <Pencil size={20} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="w-1/6">
          <Select
            value={limit.toString()}
            onValueChange={value => setLimit(Number(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 mt-2">items per page</span>
        </div>
        <Pagination
          page={page}
          totalPages={Math.ceil(total / limit)}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
